import { Injectable, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';

/**
 * Permission Error Handler Service
 * 
 * Centralized service for handling permission-related HTTP errors with user-friendly messages.
 * Provides consistent error messaging and navigation for 401, 403, and other permission errors.
 * 
 * @example
 * // In a component or service
 * this.permissionErrorHandler.handle403Error(error, 'view this project');
 * 
 * @example
 * // In HTTP error handler
 * catchError(err => {
 *   this.permissionErrorHandler.handleError(err);
 *   return throwError(() => err);
 * })
 */
@Injectable({ providedIn: 'root' })
export class PermissionErrorHandlerService {
    private _snackBar = inject(MatSnackBar);
    private _router = inject(Router);

    /**
     * Handle 401 Unauthorized error
     * Redirects to login and shows session expired message
     */
    handle401Error(): void {
        this.showError(
            'Your session has expired. Please sign in again.',
            'Session Expired'
        );
        
        // Redirect handled by auth.interceptor
    }

    /**
     * Handle 403 Forbidden error
     * Shows permission denied message and optionally navigates away
     * 
     * @param error - The HTTP error response
     * @param action - Description of the action that was denied (e.g., 'edit this project')
     * @param navigateTo - Optional route to navigate to (default: previous page)
     */
    handle403Error(error: any, action?: string, navigateTo?: string): void {
        const actionText = action ? ` to ${action}` : '';
        
        // Check if backend provided a specific message
        const backendMessage = error?.error?.message || error?.error?.error;
        
        let message = `You don't have permission${actionText}.`;
        
        if (backendMessage && typeof backendMessage === 'string') {
            message = backendMessage;
        }
        
        this.showError(message, 'Permission Denied');
        
        // Navigate to specified route or go back
        if (navigateTo) {
            setTimeout(() => {
                this._router.navigateByUrl(navigateTo);
            }, 1500);
        }
    }

    /**
     * Handle 404 Not Found error
     * Shows resource not found message
     * 
     * @param resourceType - Type of resource (e.g., 'project', 'user', 'honeytoken')
     */
    handle404Error(resourceType: string = 'resource'): void {
        this.showError(
            `The ${resourceType} you're looking for doesn't exist or has been deleted.`,
            'Not Found'
        );
    }

    /**
     * Handle generic HTTP errors with permission awareness
     * Automatically routes to appropriate handler based on status code
     * 
     * @param error - The HTTP error response
     * @param context - Optional context for better error messages
     */
    handleError(error: any, context?: { action?: string; resourceType?: string; navigateTo?: string }): void {
        if (!error) return;

        const status = error.status || error.statusCode;

        switch (status) {
            case 401:
                this.handle401Error();
                break;

            case 403:
                this.handle403Error(error, context?.action, context?.navigateTo);
                break;

            case 404:
                this.handle404Error(context?.resourceType || 'resource');
                break;

            case 422:
                this.handleValidationError(error);
                break;

            case 500:
            case 502:
            case 503:
                this.handleServerError();
                break;

            default:
                this.handleGenericError(error);
        }
    }

    /**
     * Handle 422 Validation Error
     * Shows validation messages from backend
     */
    handleValidationError(error: any): void {
        const errors = error?.error?.errors;
        
        if (errors && typeof errors === 'object') {
            // Show first validation error
            const firstError = Object.values(errors)[0];
            const message = Array.isArray(firstError) ? firstError[0] : firstError;
            this.showError(message, 'Validation Error');
        } else {
            this.showError(
                error?.error?.message || 'Please check your input and try again.',
                'Validation Error'
            );
        }
    }

    /**
     * Handle 500/502/503 Server Errors
     * Shows generic server error message
     */
    handleServerError(): void {
        this.showError(
            'A server error occurred. Please try again later.',
            'Server Error'
        );
    }

    /**
     * Handle generic/unknown errors
     */
    handleGenericError(error: any): void {
        const message = error?.error?.message || 
                       error?.message || 
                       'An unexpected error occurred. Please try again.';
        
        this.showError(message, 'Error');
    }

    /**
     * Show error snackbar with consistent styling
     */
    private showError(message: string, title?: string): void {
        const displayMessage = title ? `${title}: ${message}` : message;
        
        this._snackBar.open(displayMessage, 'Dismiss', {
            duration: 6000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: ['error-snackbar']
        });
    }

    /**
     * Show warning snackbar
     * For non-error scenarios that need user attention
     */
    showWarning(message: string, title?: string): void {
        const displayMessage = title ? `${title}: ${message}` : message;
        
        this._snackBar.open(displayMessage, 'Dismiss', {
            duration: 5000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: ['warning-snackbar']
        });
    }

    /**
     * Show info snackbar
     * For informational messages
     */
    showInfo(message: string, title?: string): void {
        const displayMessage = title ? `${title}: ${message}` : message;
        
        this._snackBar.open(displayMessage, 'OK', {
            duration: 4000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: ['info-snackbar']
        });
    }

    /**
     * Check if error is permission-related (401 or 403)
     */
    isPermissionError(error: any): boolean {
        const status = error?.status || error?.statusCode;
        return status === 401 || status === 403;
    }

    /**
     * Get user-friendly error message from HTTP error
     * Useful for displaying in forms or dialogs
     */
    getErrorMessage(error: any): string {
        if (!error) return 'An unexpected error occurred';

        const status = error.status || error.statusCode;

        switch (status) {
            case 401:
                return 'Your session has expired. Please sign in again.';
            
            case 403:
                return error?.error?.message || 
                       'You don\'t have permission to perform this action.';
            
            case 404:
                return 'The requested resource was not found.';
            
            case 422:
                const errors = error?.error?.errors;
                if (errors && typeof errors === 'object') {
                    const firstError = Object.values(errors)[0];
                    if (Array.isArray(firstError)) {
                        return firstError.length > 0 ? String(firstError[0]) : 'Validation failed.';
                    }
                    return String(firstError) || 'Validation failed.';
                }
                return error?.error?.message || 'Validation failed.';
            
            case 500:
            case 502:
            case 503:
                return 'A server error occurred. Please try again later.';
            
            default:
                return error?.error?.message || 
                       error?.message || 
                       'An unexpected error occurred.';
        }
    }
}
